// Pure helpers for the announcements system — plain JS (no JSX) so the node
// test runner can exercise the merge/visibility rules directly.
//
// Two sources feed What's New:
//   - the code-shipped list in src/data/announcements.jsx (JSX bodies,
//     function audiences)
//   - rows Jill posts from Admin > Update Users (text bodies,
//     audience_roles arrays), mirrored into a localStorage cache by a
//     fail-silent background refresh
// The offline hard rule holds because every reader consumes the merged
// bundled+cached list synchronously; only the refresher touches the network.

export const CACHE_KEY = "announcementsCache_v1";
export const SEEN_EVENT = "announcements-seen-changed";
export const SEEN_KEY = (id, uid) => `announcementSeen_${id}_${uid}`;

// A posted row -> the same shape the bundled entries use. body stays a plain
// string here; the components render it via paragraphs().
export function normalizeDbRow(row) {
  if (!row || !row.id || !row.title || !row.body) return null;
  if (row.active === false) return null;
  return {
    id: String(row.id),
    date: String(row.posted_on || "").slice(0, 10),
    type: row.type === "feature" ? "feature" : "developer",
    title: String(row.title),
    body: String(row.body),
    audienceRoles: Array.isArray(row.audience_roles) && row.audience_roles.length
      ? row.audience_roles.map((r) => String(r).toLowerCase())
      : null, // null = everyone
    posted: true, // came from the table, not the bundle
  };
}

// Bundled entries win on id collision (they shipped first); newest first,
// ties broken toward posted rows so a same-day post outranks the bundle.
export function mergeAnnouncements(bundled, dbRows) {
  const rows = (dbRows || []).map(normalizeDbRow).filter(Boolean);
  const seen = new Set((bundled || []).map((a) => a.id));
  const merged = [...(bundled || []), ...rows.filter((r) => !seen.has(r.id))];
  return merged.sort((a, b) => {
    const d = String(b.date || "").localeCompare(String(a.date || ""));
    if (d !== 0) return d;
    return (b.posted ? 1 : 0) - (a.posted ? 1 : 0);
  });
}

export function isVisibleTo(entry, { roles = [], boardPosition = null, committeePositions = [] } = {}) {
  try {
    if (typeof entry.audience === "function") {
      return !!entry.audience(roles, boardPosition, committeePositions);
    }
    if (entry.audienceRoles == null) return true;
    const mine = (roles || []).map((r) => String(r).toLowerCase());
    // system_admin sees everything she posts, whatever the audience
    if (mine.includes("system_admin")) return true;
    return entry.audienceRoles.some((r) => mine.includes(r));
  } catch {
    return false;
  }
}

// Plain-text body -> paragraph strings. Every line break starts a new
// paragraph (Jill 2026-09-07: she presses Enter once between paragraphs and
// the old blank-line-only rule ran her notes together into one block). A
// blank line still works; runs of empty lines never produce empty paragraphs.
export function paragraphs(text) {
  return String(text || "")
    .split(/\r?\n/)
    .map((p) => p.trim())
    .filter(Boolean);
}

export function readCache(storage) {
  try {
    const raw = (storage || localStorage).getItem(CACHE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function writeCache(rows, storage) {
  try {
    (storage || localStorage).setItem(CACHE_KEY, JSON.stringify(rows || []));
    return true;
  } catch {
    return false; // private mode / full disk — cache just stays stale
  }
}
